<script setup lang="ts">
import { ref, watch } from 'vue'
import * as Sentry from '@sentry/vue'
import { trackEvent } from '../utils/analytics'
import { philosopherChatService } from '../services/PhilosopherChatService'

// Define props for the component
const props = defineProps<{
  side: 'left' | 'right'
  isVisible: boolean
  philosopherName: string
  philosopherAlignment: string
  assistantId: string
  corpusTitles: string[]
}>()

// Define emits for events
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'fetch-titles', assistantId: string, side: string): void
}>()

// Component state
const showTitleSummary = ref(false)
const currentTitleSummary = ref<{ title: string; summary: string } | null>(null)
const loadingSummary = ref(false)
const loadingCorpus = ref(false)

// Function to close the modal
const closeModal = () => {
  emit('close')
}

// Function to request title summary from the pinecone assistant
async function requestTitleSummary(title: string) {
  try {
    // Track the summary request
    try {
      trackEvent('Summary requested', {
        side: props.side
      })
    } catch (err) {
      // Silent catch to prevent tracking failures from affecting app functionality
      console.error('Failed to track summary request event:', err)
    }
    
    // Set loading state
    loadingSummary.value = true
    showTitleSummary.value = true
    
    // Determine philosopher based on assistant ID
    const philosopher = props.assistantId as 'adam-smith' | 'karl-marx'
    
    // Use the new service to get the summary
    const summary = await philosopherChatService.getCorpusSummary(title, philosopher)
    
    // Store the summary
    currentTitleSummary.value = { title, summary }
    loadingSummary.value = false
    
    // Track summary view in Sentry
    try {
      Sentry.setContext("title_summary", {
        philosopher: props.philosopherName,
        title: title,
        assistant_id: props.assistantId,
        timestamp: new Date().toISOString()
      })
      
      Sentry.captureMessage('Title summary viewed', {
        level: 'info'
      })
    } catch (err) {
      console.error('Failed to track title summary view event:', err)
    }
  } catch (error) {
    // Handle error and reset loading state
    console.error('Error requesting title summary:', error)
    
    currentTitleSummary.value = { 
      title, 
      summary: 'Sorry, there was an error fetching the summary. Please try again.' 
    }
    loadingSummary.value = false
  }
}

// Function to go back to corpus list from summary view
function backToCorpusList() {
  showTitleSummary.value = false
  currentTitleSummary.value = null
}

// Watch for visibility changes to trigger events
watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    handleVisibilityChange()
  }
}, { immediate: true })

// Fetch titles when modal becomes visible
function handleVisibilityChange() {
  if (props.isVisible) {
    try {
      // Track with Segment.io
      trackEvent('View corpus clicked', {
        philosopher: props.philosopherName,
        alignment: props.philosopherAlignment,
        assistant_id: props.assistantId,
        side: props.side,
        timestamp: new Date().toISOString()
      })
    } catch (err) {
      // Silent catch to prevent tracking failures from affecting app functionality
      console.error('Failed to track corpus view event:', err)
    }
    
    // Fetch corpus titles after tracking
    emit('fetch-titles', props.assistantId, props.philosopherName)
  }
}
</script>

<template>
  <div v-if="isVisible" class="corpus-modal-overlay" @click="closeModal">
    <div :class="['corpus-modal', side === 'left' ? 'left-corpus-modal' : 'right-corpus-modal']" @click.stop>
      <div class="corpus-modal-header">
        <h3>{{ philosopherName }}'s Corpus</h3>
        <button class="modal-close-button" @click="closeModal">×</button>
      </div>
      <div class="corpus-modal-content">
        <!-- Loading state -->
        <div v-if="loadingCorpus" class="corpus-loading">
          <div class="corpus-spinner"></div>
          <p>Loading corpus information...</p>
        </div>
        
        <!-- Summary view -->
        <div v-else-if="showTitleSummary" class="corpus-summary">
          <!-- Back button -->
          <button class="summary-back-button" @click="backToCorpusList">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to corpus list
          </button>
          
          <!-- Summary title -->
          <h4 class="summary-title">{{ currentTitleSummary?.title }}</h4>
          
          <!-- Loading spinner for summary -->
          <div v-if="loadingSummary" class="corpus-loading">
            <div class="corpus-spinner"></div>
            <p>Generating summary...</p>
          </div>
          
          <!-- Summary content -->
          <div v-else class="summary-content">
            <p v-for="(paragraph, index) in currentTitleSummary?.summary.split('\n\n')" :key="index">
              {{ paragraph }}
            </p>
            <p class="ai-byline">Generated by Claude AI</p>
          </div>
        </div>
        
        <!-- Corpus list -->
        <div v-else class="corpus-list">
          <h4>Trained on {{ corpusTitles.length }} {{ corpusTitles.length === 1 ? 'source' : 'sources' }}:</h4>
          <ul>
            <li v-for="(title, index) in corpusTitles" :key="index" class="corpus-list-item">
              <div class="corpus-title">{{ title }}</div>
              <button 
                class="summarize-button" 
                @click.stop="requestTitleSummary(title)"
                title="Get AI summary of this title"
              >
                Summarize
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.corpus-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.corpus-modal {
  background-color: #fff;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.left-corpus-modal {
  border-top: 4px solid #4070c0;
}

.right-corpus-modal {
  border-top: 4px solid #c04040;
}

.corpus-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.corpus-modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.modal-close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #555;
  cursor: pointer;
}

.corpus-modal-content {
  padding: 16px;
  overflow-y: auto;
}

.corpus-list h4 {
  margin-top: 0;
  color: #444;
  font-weight: 500;
}

.corpus-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #eee;
}

.corpus-title {
  font-weight: 500;
  color: #333;
}

.summarize-button {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.9rem;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
}

.summarize-button:hover {
  background-color: #e0e0e0;
  color: #333;
}

.corpus-summary {
  display: flex;
  flex-direction: column;
}

.summary-back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #4070c0;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  align-self: flex-start;
}

.summary-title {
  font-size: 1.2rem;
  color: #333;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.summary-content {
  line-height: 1.6;
  color: #333;
}

.summary-content p {
  margin-bottom: 1em;
}

.ai-byline {
  margin-top: 24px;
  font-style: italic;
  color: #888;
  font-size: 0.9rem;
  text-align: right;
}

.corpus-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.corpus-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #4070c0;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.corpus-loading p {
  color: #666;
  font-size: 0.9rem;
}

/* Mobile specific adjustments */
@media (max-width: 768px) {
  .corpus-modal {
    width: 95%;
    max-height: 85vh;
  }
  
  .corpus-list-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .summarize-button {
    align-self: flex-end;
  }
}
</style>